import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { NAV_LINKS, BRAND } from '../data/mock';
import { Button } from './ui/button';

import { img } from '../lib/img';

function DesktopItem({ link }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  if (!link.children) {
    return (
      <NavLink
        to={link.to}
        end={link.to === '/'}
        className={({ isActive }) =>
          `text-[12px] tracking-[0.22em] uppercase transition-colors duration-300 ${
            isActive ? 'text-champagne-deep' : 'text-charcoal hover:text-champagne-deep'
          }`
        }
      >
        {link.label}
      </NavLink>
    );
  }

  const onEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const onLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <button
        type="button"
        data-testid={`nav-${link.label.toLowerCase()}-trigger`}
        className="flex items-center gap-1.5 text-[12px] tracking-[0.22em] uppercase text-charcoal hover:text-champagne-deep transition-colors duration-300"
      >
        {link.label} <ChevronDown size={12} className={`transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div
        className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 transition-opacity duration-200 ${
          open ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <div className="min-w-[280px] bg-ivory border border-ivory-deep shadow-[0_30px_60px_-30px_rgba(0,0,0,0.25)] py-3">
          {link.children.map((c) => (
            <Link
              key={c.label}
              to={c.to}
              data-testid={`nav-services-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="block px-6 py-2.5 text-[11px] tracking-[0.22em] uppercase text-charcoal/80 hover:text-champagne-deep hover:bg-ivory-warm transition-colors"
            >
              {c.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileServices, setMobileServices] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMobileServices(false); }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled ? 'bg-ivory/95 backdrop-blur-md border-b border-ivory-deep' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-logo">
          <img
            loading="eager"
            decoding="async"
            fetchPriority="high"
            src={img(BRAND.logo, 1000)}
            alt="Mondaine Style — Personal Styling For Modern Men"
            className="h-14 md:h-16 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
          {NAV_LINKS.map((l) => <DesktopItem key={l.label} link={l} />)}
        </nav>

        <div className="hidden lg:block">
          <Link to="/book-consultation">
            <Button data-testid="nav-book-consultation-btn" className="bg-charcoal hover:bg-charcoal-soft text-ivory rounded-none px-6 py-5 text-[11px] tracking-[0.22em] uppercase">
              Book Consultation
            </Button>
          </Link>
        </div>

        <button
          className="lg:hidden text-charcoal"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-y-auto transition-all duration-500 ${
          open ? 'max-h-[80vh] border-t border-ivory-deep' : 'max-h-0'
        } bg-ivory`}
      >
        <div className="px-6 py-8 flex flex-col gap-5">
          {NAV_LINKS.map((l) => {
            if (!l.children) {
              return (
                <NavLink
                  key={l.label}
                  to={l.to}
                  end={l.to === '/'}
                  className={({ isActive }) =>
                    `font-serif text-2xl ${isActive ? 'text-champagne-deep' : 'text-charcoal'}`
                  }
                >
                  {l.label}
                </NavLink>
              );
            }
            return (
              <div key={l.label}>
                <button
                  type="button"
                  onClick={() => setMobileServices((v) => !v)}
                  className="flex items-center justify-between w-full font-serif text-2xl text-charcoal"
                >
                  {l.label} <ChevronDown size={20} className={`transition-transform ${mobileServices ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${mobileServices ? 'max-h-96 mt-3' : 'max-h-0'}`}>
                  <div className="pl-4 border-l border-charcoal/15 flex flex-col gap-3">
                    {l.children.map((c) => (
                      <Link
                        key={c.label}
                        to={c.to}
                        className="text-sm tracking-[0.18em] uppercase text-charcoal/70 hover:text-champagne-deep"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
          <Link to="/book-consultation" className="mt-2">
            <Button className="w-full bg-charcoal hover:bg-charcoal-soft text-ivory rounded-none py-6 text-[11px] tracking-[0.22em] uppercase">
              Book Consultation
            </Button>
          </Link>
          <a href={`tel:${BRAND.phone.replace(/\s/g,'')}`} className="text-xs tracking-[0.22em] uppercase text-charcoal/70 pt-2">
            {BRAND.phone}
          </a>
        </div>
      </div>
    </header>
  );
}
